import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const Signup = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { signup } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError(""); // Clear error when user types

    // Clear specific field validation error
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name) {
      errors.name = "नाम आवश्यक है";
    } else if (formData.name.length < 2) {
      errors.name = "नाम कम से कम 2 अक्षर का होना चाहिए";
    }

    if (!formData.email) {
      errors.email = "ईमेल आवश्यक है";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "वैध ईमेल पता दर्ज करें";
    }

    if (!formData.password) {
      errors.password = "पासवर्ड आवश्यक है";
    } else if (formData.password.length < 6) {
      errors.password = "पासवर्ड कम से कम 6 अक्षर का होना चाहिए";
    }

    if (!formData.confirmPassword) {
      errors.confirmPassword = "पासवर्ड पुष्टि आवश्यक है";
    } else if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "पासवर्ड मेल नहीं खाते";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("पासवर्ड मेल नहीं खाते");
      setLoading(false);
      return;
    }

    if (formData.password.length < 6) {
      setError("पासवर्ड कम से कम 6 अक्षर का होना चाहिए");
      setLoading(false);
      return;
    }

    try {
      const result = await signup(
        formData.name,
        formData.email,
        formData.password
      );
      if (result.success) {
        navigate("/dashboard");
      } else {
        setError(result.message);
      }
    } catch (error) {
      setError("पंजीकरण में त्रुटि हुई। कृपया पुनः प्रयास करें।");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-temple-bg flex items-center justify-center py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="text-4xl sm:text-6xl mb-4">🕉️</div>
          <h2 className="text-2xl sm:text-3xl font-serif font-bold text-temple-text">
            साइनअप करें
          </h2>
          <p className="mt-2 text-temple-text-muted text-sm sm:text-base">
            नया खाता बनाएं
          </p>
        </div>

        {/* Signup Form */}
        <div className="bg-temple-bg-warm rounded-3xl shadow-temple p-6 sm:p-8 border border-temple-primary/10">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-bold text-temple-text mb-2">
                पूरा नाम
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className={`w-full p-3 border-2 rounded-lg focus:ring-0 transition bg-temple-bg ${
                  validationErrors.name
                    ? "border-red-300 focus:border-red-500"
                    : "border-temple-primary/20 focus:border-temple-primary"
                }`}
                placeholder="आपका पूरा नाम"
              />
              {validationErrors.name && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.name}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-temple-text mb-2">
                ईमेल पता
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`w-full p-3 border-2 rounded-lg focus:ring-0 transition bg-temple-bg ${
                  validationErrors.email
                    ? "border-red-300 focus:border-red-500"
                    : "border-temple-primary/20 focus:border-temple-primary"
                }`}
                placeholder="आपका ईमेल पता"
              />
              {validationErrors.email && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.email}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-temple-text mb-2">
                पासवर्ड
              </label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`w-full p-3 pr-12 border-2 rounded-lg focus:ring-0 transition bg-temple-bg ${
                    validationErrors.password
                      ? "border-red-300 focus:border-red-500"
                      : "border-temple-primary/20 focus:border-temple-primary"
                  }`}
                  placeholder="कम से कम 6 अक्षर का पासवर्ड"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-temple-text-muted hover:text-temple-primary"
                >
                  <i
                    className={`fas ${
                      showPassword ? "fa-eye-slash" : "fa-eye"
                    }`}
                  ></i>
                </button>
              </div>
              {validationErrors.password && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.password}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-bold text-temple-text mb-2">
                पासवर्ड की पुष्टि करें
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className={`w-full p-3 pr-12 border-2 rounded-lg focus:ring-0 transition bg-temple-bg ${
                    validationErrors.confirmPassword
                      ? "border-red-300 focus:border-red-500"
                      : "border-temple-primary/20 focus:border-temple-primary"
                  }`}
                  placeholder="पासवर्ड दोबारा लिखें"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-temple-text-muted hover:text-temple-primary"
                >
                  <i
                    className={`fas ${
                      showConfirmPassword ? "fa-eye-slash" : "fa-eye"
                    }`}
                  ></i>
                </button>
              </div>
              {validationErrors.confirmPassword && (
                <p className="mt-1 text-sm text-red-600">
                  {validationErrors.confirmPassword}
                </p>
              )}
            </div>

            <div className="flex items-center">
              <input
                id="terms"
                name="terms"
                type="checkbox"
                required
                className="h-4 w-4 text-temple-primary focus:ring-temple-primary border-temple-primary/30 rounded"
              />
              <label
                htmlFor="terms"
                className="ml-2 block text-sm text-temple-text-muted"
              >
                मैं{" "}
                <Link
                  to="#"
                  className="text-temple-primary hover:text-temple-primary-dark font-medium"
                >
                  नियम और शर्तों
                </Link>{" "}
                से सहमत हूँ
              </label>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full btn-temple-primary py-3 px-4 rounded-xl font-bold text-base sm:text-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <span className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-temple-bg border-t-transparent mr-2"></div>
                  पंजीकरण हो रहा है...
                </span>
              ) : (
                "साइनअप करें"
              )}
            </button>

            <div className="text-center">
              <p className="text-sm text-temple-text-muted">
                पहले से खाता है?{" "}
                <Link
                  to="/login"
                  className="text-temple-primary hover:text-temple-primary-dark font-bold"
                >
                  लॉगिन करें
                </Link>
              </p>
            </div>
          </form>
        </div>

        {/* Benefits */}
        <div className="bg-temple-bg-soft rounded-2xl p-4 sm:p-6 border border-temple-secondary/20">
          <h3 className="text-base sm:text-lg font-bold text-temple-text mb-4 text-center">
            सदस्यता के लाभ
          </h3>
          <ul className="space-y-2 text-sm text-temple-text-muted">
            <li className="flex items-center">
              <span className="mr-2 text-temple-primary">✓</span>
              व्यक्तिगत डैशबोर्ड और प्रोफाइल
            </li>
            <li className="flex items-center">
              <span className="mr-2 text-temple-primary">✓</span>
              दान का पूरा इतिहास और रसीदें
            </li>
            <li className="flex items-center">
              <span className="mr-2 text-temple-primary">✓</span>
              विशेष कार्यक्रमों की प्राथमिकता जानकारी
            </li>
            <li className="flex items-center">
              <span className="mr-2 text-temple-primary">✓</span>
              समुदाय से जुड़ाव और चर्चा
            </li>
            <li className="flex items-center">
              <span className="mr-2 text-temple-primary">✓</span>
              व्यक्तिगत सहायता और मार्गदर्शन
            </li>
          </ul>
        </div>

        {/* Privacy Note */}
        <div className="bg-green-50 border border-green-200 rounded-2xl p-4">
          <div className="flex items-center">
            <div className="text-green-600 mr-3">🔒</div>
            <div>
              <h4 className="text-sm font-bold text-green-800">
                गोपनीयता सुरक्षित
              </h4>
              <p className="text-xs text-green-700 mt-1">
                आपकी व्यक्तिगत जानकारी पूर्णतः सुरक्षित है और किसी तीसरे पक्ष के
                साथ साझा नहीं की जाती।
              </p>
            </div>
          </div>
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <Link
            to="/"
            className="text-temple-primary hover:text-temple-primary-dark font-medium text-sm inline-flex items-center"
          >
            <span className="mr-1">←</span>
            होम पेज पर वापस जाएं
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Signup;
